// Which MenuSliderBar items (and back/filter/quick-menu buttons) a page shows.
//
// Priority: page exception (menuSliderBar.pageOverrides) > item placement
// (menuSliderBar.swiperItems[].placement) > existing defaults. An item without
// `placement` is shown on every page, so every configuration saved before
// this feature renders exactly as before. Mirrored server-side by
// server_elakay/elakay_helpers/navigationDisplayRules.js.
import { elakayAutoText } from "../pageInside/dataElakay";

const exact = (path) => (pathname) => pathname === path;
const prefix = (path) => (pathname) => pathname === path || pathname.startsWith(`${path}/`);

// Real routes of Routes.js. `match` receives a lower-cased, trailing-slash-free pathname.
export const MENU_SLIDER_PAGES = [
  { key: "home", match: exact("/"), labelKey: "h_msp_page_home", fallback: "Home" },
  { key: "dealToday", match: exact("/deal-today"), labelKey: "h_msp_page_deal_today", fallback: "Today's Deal" },
  { key: "bestSellers", match: exact("/best-sellers"), labelKey: "h_msp_page_best_sellers", fallback: "Best Sellers" },
  { key: "newProducts", match: exact("/new-product"), labelKey: "h_msp_page_new_products", fallback: "New products" },
  { key: "allCategories", match: exact("/all-catageories"), labelKey: "h_msp_page_all_categories", fallback: "All Categories" },
  { key: "search", match: exact("/search"), labelKey: "h_msp_page_search", fallback: "Search results" },
  { key: "productPreview", match: prefix("/product/preview"), labelKey: "h_msp_page_product_preview", fallback: "Product page" },
  { key: "shop", match: prefix("/lakay-shopseller"), labelKey: "h_msp_page_shop", fallback: "Seller shops" },
  { key: "helpContact", match: exact("/help-contact"), labelKey: "h_msp_page_help_contact", fallback: "Help & Contact" },
  { key: "investments", match: prefix("/investments"), labelKey: "h_msp_page_investments", fallback: "Investments" },
  { key: "jobs", match: prefix("/jobs"), labelKey: "h_msp_page_jobs", fallback: "Jobs" },
  { key: "howWeDoIt", match: prefix("/how-we-do-it"), labelKey: "h_msp_page_how_we_do_it", fallback: "How We Do It" },
  { key: "cart", match: exact("/cart"), labelKey: "h_msp_page_cart", fallback: "Cart" },
  { key: "profile", match: exact("/profile"), labelKey: "h_msp_page_profile", fallback: "Profile" },
  { key: "orders", match: (pathname) => /^\/(orders|purchased)(\/|$)/.test(pathname), labelKey: "h_msp_page_orders", fallback: "Orders & purchases" },
  { key: "myShop", match: prefix("/myshop"), labelKey: "h_msp_page_my_shop", fallback: "My Shop" },
  { key: "yourStep", match: (pathname) => pathname === "/your-step" || pathname === "/user-step", labelKey: "h_msp_page_your_step", fallback: "Your Step" },
  { key: "userViewed", match: exact("/user-viewed"), labelKey: "h_msp_page_user_viewed", fallback: "Viewing Activity" },
  { key: "provider", match: prefix("/provider"), labelKey: "h_msp_page_provider", fallback: "Provider Center" },
];

export const menuSliderPageLabel = (key) => {
  const page = MENU_SLIDER_PAGES.find((entry) => entry.key === key);
  return page ? elakayAutoText(page.labelKey, page.fallback) : key;
};

const OBJECT_ID = /^[a-f0-9]{24}$/i;

export const normalizeMenuSliderPath = (value) => {
  const raw = String(value || "").split(/[?#]/)[0].trim();
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return "";
  return raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
};

// { path, pageKey, categoryId } of the current route. A category page is
// /product/<categoryId> (never /product/preview/<productId>).
export const resolveMenuSliderPageContext = (pathname) => {
  const path = normalizeMenuSliderPath(pathname) || "/";
  const lower = path.toLowerCase();
  const categoryMatch = /^\/product\/([a-f0-9]{24})$/i.exec(path);
  const categoryId = categoryMatch ? categoryMatch[1] : null;
  const page = categoryId ? null : MENU_SLIDER_PAGES.find((entry) => entry.match(lower));
  return { path, pageKey: page?.key || null, categoryId };
};

export const normalizeMenuItemPlacement = (placement) => {
  const scope = ["all", "home", "categories", "pages"].includes(placement?.scope) ? placement.scope : "all";
  const list = (value) => (Array.isArray(value) ? [...new Set(value.filter(Boolean).map(String))] : []);
  return {
    scope,
    categoryIds: list(placement?.categoryIds).filter((id) => OBJECT_ID.test(id)),
    pages: list(placement?.pages).filter((key) => MENU_SLIDER_PAGES.some((page) => page.key === key)),
    paths: list(placement?.paths).map(normalizeMenuSliderPath).filter(Boolean),
  };
};

// Global (item-level) visibility. "categories" with no selected category =
// every category page.
export const menuItemMatchesPage = (item, context) => {
  const placement = normalizeMenuItemPlacement(item?.placement);
  switch (placement.scope) {
    case "home": return context.pageKey === "home";
    case "categories":
      return Boolean(context.categoryId) && (!placement.categoryIds.length || placement.categoryIds.includes(context.categoryId));
    case "pages":
      return Boolean(context.pageKey && placement.pages.includes(context.pageKey))
        || placement.paths.some((path) => path.toLowerCase() === context.path.toLowerCase());
    default: return true;
  }
};

// The most specific enabled exception of this page: exact path, then the
// category, then the page type, then "every category page".
export const findMenuSliderPageOverride = (overrides, context) => {
  const list = (Array.isArray(overrides) ? overrides : []).filter((entry) => entry && entry.enabled !== false);
  const candidates = [
    `path:${context.path}`,
    context.categoryId ? `category:${context.categoryId}` : null,
    context.pageKey,
    context.categoryId ? "categories" : null,
  ].filter(Boolean);
  for (const target of candidates) {
    const found = list.find((entry) => String(entry.target || "").toLowerCase() === target.toLowerCase());
    if (found) return found;
  }
  return null;
};

const applyOrder = (items, order) => {
  if (!Array.isArray(order) || !order.length) return items;
  const rank = new Map(order.map((id, index) => [id, index]));
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ra = a.item?.id && rank.has(a.item.id) ? rank.get(a.item.id) : Number.MAX_SAFE_INTEGER;
      const rb = b.item?.id && rank.has(b.item.id) ? rank.get(b.item.id) : Number.MAX_SAFE_INTEGER;
      return ra - rb || a.index - b.index;
    })
    .map(({ item }) => item);
};

const DEFAULT_BUTTONS = Object.freeze({ back: "inherit", filter: "inherit", quickMenu: "inherit" });

// Items saved before ids existed get a positional id, computed identically
// by the editor and the renderer; the editor persists it on the next save.
export const withStableMenuItemIds = (items, prefix = "msi_legacy_") => (Array.isArray(items) ? items : []).map((item, index) => (
  item?.id ? item : { ...item, id: `${prefix}${index}` }
));

export const menuItemIdsVisibleWithOverride = (items, override, context) => {
  const hidden = new Set(Array.isArray(override?.hiddenItemIds) ? override.hiddenItemIds : []);
  const included = new Set(Array.isArray(override?.includedItemIds) ? override.includedItemIds : []);
  return withStableMenuItemIds(items)
    .filter((item) => (menuItemMatchesPage(item, context) ? !hidden.has(item.id) : included.has(item.id)))
    .map((item) => item.id);
};

// Tick / untick one general item for a page exception.
export const toggleMenuItemForOverride = (override, item, context, visible) => {
  const hidden = new Set(override?.hiddenItemIds || []);
  const included = new Set(override?.includedItemIds || []);
  if (menuItemMatchesPage(item, context)) {
    if (visible) hidden.delete(item.id); else hidden.add(item.id);
  } else if (visible) included.add(item.id); else included.delete(item.id);
  return { ...override, hiddenItemIds: [...hidden], includedItemIds: [...included] };
};

// items: the global list (unified swiperItems or the legacy composition).
export const resolveMenuSliderItems = (items, pageOverrides, context) => {
  const base = withStableMenuItemIds(items);
  const override = findMenuSliderPageOverride(pageOverrides, context);
  if (!override) {
    return { items: base.filter((item) => menuItemMatchesPage(item, context)), buttons: DEFAULT_BUTTONS, override: null };
  }
  const buttons = { ...DEFAULT_BUTTONS, ...(override.buttons || {}) };
  if (override.mode === "hide") return { items: [], buttons, override };
  const pageOnly = Array.isArray(override.items) ? override.items : [];
  if (override.mode === "replace") return { items: applyOrder(pageOnly, override.order), buttons, override };
  // "customize": the items placed on this page minus the ones unticked for
  // it, plus items of other pages explicitly ticked for it. An item added
  // later follows its own placement here too.
  const visible = new Set(menuItemIdsVisibleWithOverride(base, override, context));
  const globalItems = base.filter((item) => visible.has(item.id));
  return { items: applyOrder([...globalItems, ...pageOnly], override.order), buttons, override };
};

// Back / filter / quick-menu after a page exception. `filter` can only be
// hidden: the filter drawer exists only on pages that mount it.
export const resolveMenuSliderButtons = (buttons, defaults) => ({
  back: buttons?.back === "hide" ? false : buttons?.back === "show" ? true : defaults.back,
  filter: buttons?.filter === "hide" ? false : defaults.filter,
  quickMenu: buttons?.quickMenu === "hide" ? false : buttons?.quickMenu === "show" ? true : defaults.quickMenu,
});

let idCounter = 0;
export const createMenuItemId = () => {
  idCounter += 1;
  return `msi_${Date.now().toString(36)}${idCounter.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
};

// "/deal-today" and "https://<this site>/deal-today/" are the same link.
export const menuItemDuplicateKey = (item) => {
  if (item?.itemType === "category" && item?.categoryId) return `category:${item.categoryId}`;
  const destination = String(item?.destination || "").trim();
  if (!destination) return "";
  try {
    const url = new URL(destination, "https://elakay.local");
    const sameSite = url.hostname === "elakay.local" || (typeof window !== "undefined" && url.hostname === window.location.hostname);
    const path = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, "") : url.pathname;
    return sameSite ? `link:${path.toLowerCase()}${url.search}` : `link:${url.href.toLowerCase()}`;
  } catch {
    return `link:${destination.toLowerCase()}`;
  }
};

export const findDuplicateMenuItems = (items) => {
  const seen = new Map();
  const duplicates = new Set();
  (Array.isArray(items) ? items : []).forEach((item, index) => {
    const key = menuItemDuplicateKey(item);
    if (!key) return;
    if (seen.has(key)) { duplicates.add(index); duplicates.add(seen.get(key)); } else seen.set(key, index);
  });
  return duplicates;
};

// "empty" (plain text), "valid", or "invalid" (javascript:, //host, ...).
export const validateMenuDestination = (destination) => {
  const raw = String(destination || "").trim();
  if (!raw) return "empty";
  if (raw.startsWith("/")) return raw.startsWith("//") || raw.includes("\\") ? "invalid" : "valid";
  try {
    const url = new URL(raw);
    return ["http:", "https:"].includes(url.protocol) ? "valid" : "invalid";
  } catch {
    return "invalid";
  }
};

// Device of a preview / viewport width, same breakpoints as menubarSlider.css.
export const menuSliderDeviceForWidth = (width) => (width <= 700 ? "mobile" : width <= 1024 ? "tablet" : "desktop");

export const menuItemVisibleOnDevice = (item, device) => item?.devices?.[device] !== false;
